import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Rocket, 
  Search, 
  Users, 
  Lightbulb, 
  Target,
  Zap,
  Shield,
  Globe,
  ArrowRight,
  Star,
  TrendingUp,
  Code,
  Palette,
  BarChart3,
  Video,
  Mic,
  Database,
  Cpu,
  Brain,
  CheckCircle,
  Award,
  Heart,
  Sparkles
} from 'lucide-react';

// Type definitions for better TypeScript support
interface FeatureItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface UserType {
  icon: React.ReactNode;
  role: string;
  description: string;
}

interface ToolCategory {
  name: string;
  count: string;
  color: string;
  icon: string;
}

interface StatItem {
  number: string;
  label: string;
}

// Constants outside component to prevent recreation
const STATS: StatItem[] = [
  { number: "25,000+", label: "AI Tools" },
  { number: "150+", label: "Categories" },
  { number: "50,000+", label: "Monthly Users" },
  { number: "95%", label: "Satisfaction Rate" }
];

const FEATURES: FeatureItem[] = [
  {
    icon: <Search className="w-6 h-6" />,
    title: "Smart Discovery",
    description: "Advanced filtering by category, use case, and features to find exactly what you need"
  },
  {
    icon: <Zap className="w-6 h-6" />,
    title: "Real-time Updates",
    description: "Always current with the latest AI tools and market trends"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Trusted Reviews",
    description: "Verified user reviews and expert analysis"
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: "Global Community",
    description: "Join thousands of AI enthusiasts and professionals"
  }
];

const USER_TYPES: UserType[] = [
  {
    icon: <Code className="w-5 h-5" />,
    role: "Developers",
    description: "Find APIs, code assistants, and development tools"
  },
  {
    icon: <Palette className="w-5 h-5" />,
    role: "Designers",
    description: "Discover AI-powered design and creative tools"
  },
  {
    icon: <BarChart3 className="w-5 h-5" />,
    role: "Marketers",
    description: "Automate and optimize your marketing efforts"
  },
  {
    icon: <Video className="w-5 h-5" />,
    role: "Content Creators",
    description: "Enhance your content with AI writing and media tools"
  },
  {
    icon: <TrendingUp className="w-5 h-5" />,
    role: "Entrepreneurs",
    description: "Scale your business with AI solutions"
  },
  {
    icon: <Mic className="w-5 h-5" />,
    role: "Researchers",
    description: "Advanced tools for data analysis and research"
  }
];

const TOOL_CATEGORIES: ToolCategory[] = [
  { name: "AI Writing & Content", count: "2,500+", color: "from-blue-500 to-cyan-500", icon: "✍️" },
  { name: "AI Image Generators", count: "1,200+", color: "from-purple-500 to-pink-500", icon: "🎨" },
  { name: "AI Coding Assistants", count: "800+", color: "from-green-500 to-emerald-500", icon: "💻" },
  { name: "Voice & Video AI", count: "900+", color: "from-orange-500 to-red-500", icon: "🎬" },
  { name: "Research & Analytics", count: "1,100+", color: "from-indigo-500 to-blue-500", icon: "📊" },
  { name: "No-code AI Tools", count: "1,500+", color: "from-teal-500 to-green-500", icon: "🚀" }
];

const ABOUT_DATA = {
  hero: {
    tagline: "The World's Largest AI Tools Directory",
    title: "About TooliQ.ai",
    description: "Your intelligent gateway to the world of AI applications. Whether you're building, scaling, or exploring—we help you find the perfect AI tools to transform your workflow and accelerate innovation.",
    seoContent: "TooliQ.ai is the world's largest AI tools directory with 25,000+ tools across 150+ categories. Founded in 2024, our platform helps developers, businesses, creators, and AI enthusiasts discover, compare, and implement the perfect AI solutions. We serve 50,000+ monthly users with a 95% satisfaction rate."
  },
  mission: {
    title: "Our Mission & Vision",
    description: "At TooliQ.ai, our mission is to democratize AI adoption by providing a comprehensive, intuitive platform where anyone—from developers to business leaders—can discover, compare, and implement the right AI tools with confidence.",
    vision: "We believe that artificial intelligence should be accessible to everyone. Our vision is to create the most trusted AI tools directory in the world, helping users make informed decisions about which AI solutions best fit their needs."
  },
  whyChoose: [
    "25,000+ carefully curated AI tools",
    "Updated daily with new innovations",
    "Unbiased reviews and comparisons",
    "Free and premium tool options",
    "Community-driven recommendations",
    "Advanced search and filtering"
  ]
};

const About: React.FC = () => {
  // SEO data - memoized to prevent recreation
  const seoData = useMemo(() => ({
    title: "About TooliQ.ai | Discover 25,000+ AI Tools & Solutions | AI Tools Directory",
    description: "TooliQ.ai is the world's largest AI tools directory with 25,000+ tools across 150+ categories. Learn about our mission to democratize AI adoption. Join 50,000+ monthly users.",
    keywords: "AI tools directory, artificial intelligence tools, AI software, machine learning tools, AI comparison, TooliQ about, AI tools platform, find AI applications, AI solutions, AI tools 2024",
    canonical: "https://tooliq.ai/about",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "About TooliQ.ai - The Ultimate AI Tools Directory",
      "description": "TooliQ.ai is the world's largest AI tools directory with 25,000+ tools across 150+ categories.",
      "url": "https://tooliq.ai/about",
      "publisher": {
        "@type": "Organization",
        "name": "TooliQ.ai",
        "description": "The ultimate AI tools directory with 25,000+ tools across 150+ categories",
        "url": "https://tooliq.ai",
        "logo": "https://tooliq.ai/logo.png",
        "sameAs": [
          "https://twitter.com/TooliQAI",
          "https://www.linkedin.com/company/tooliq-ai"
        ],
        "foundingDate": "2024",
        "numberOfEmployees": "5-10",
        "slogan": "Your intelligent gateway to the world of AI applications"
      }
    }
  }), []);

  // Render functions for better organization
  const renderStats = () => (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
      {STATS.map((stat, index) => (
        <div key={index} className="text-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700">
          <div className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            {stat.number}
          </div>
          <div className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );

  const renderFeatures = () => (
    <div className="grid sm:grid-cols-2 gap-4">
      {FEATURES.map((feature, index) => (
        <div key={index} className="flex items-start gap-3 p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl hover:bg-white/80 dark:hover:bg-gray-800/80 transition-colors">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400 flex-shrink-0">
            {feature.icon}
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-1">
              {feature.title}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {feature.description}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderWhyChooseList = () => (
    <div className="space-y-4">
      {ABOUT_DATA.whyChoose.map((item, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-3 h-3 text-white" />
          </div>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );

  const renderToolCategories = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {TOOL_CATEGORIES.map((category, index) => (
        <div
          key={index}
          className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:border-transparent hover:-translate-y-1"
        >
          <div className={`w-12 h-12 bg-gradient-to-r ${category.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
            <span className="text-xl text-white">{category.icon}</span>
          </div>
          <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
            {category.name}
          </h3>
          <p className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent">
            {category.count}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">
            AI tools available
          </p>
        </div>
      ))}
    </div>
  );

  const renderUserTypes = () => (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {USER_TYPES.map((user, index) => (
        <div
          key={index}
          className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:-translate-y-1"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
              {user.icon}
            </div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-200">
              {user.role}
            </h3>
          </div>
          <p className="text-slate-600 dark:text-slate-400">
            {user.description}
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonical} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Open Graph */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://tooliq.ai/og-images/about-tooliq.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content="https://tooliq.ai/og-images/about-tooliq.webp" />

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(seoData.structuredData)
          }}
        />
      </Helmet>

      {/* Hero Section */}
      <HeroSection />
      
      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-6 pb-20">
        {/* Stats Section */}
        {renderStats()}

        {/* Mission Section */}
        <MissionSection renderFeatures={renderFeatures} renderWhyChooseList={renderWhyChooseList} />

        {/* Tool Categories */}
        <ToolCategoriesSection renderToolCategories={renderToolCategories} />

        {/* User Types */}
        <UserTypesSection renderUserTypes={renderUserTypes} />

        {/* Technology Stack */}
        <TechnologyStackSection />

        {/* Values Section */}
        <ValuesSection />

        {/* Newsletter Section */}
        <NewsletterSection />

        {/* CTA Section */}
        <CTASection />
      </div>
    </div>
  );
};

// Extracted HeroSection component
const HeroSection: React.FC = () => (
  <div className="relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-slate-200 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))] dark:bg-grid-slate-800"></div>
    <div className="relative max-w-7xl mx-auto px-6 py-20 text-center">
      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
        <Sparkles className="w-4 h-4" />
        {ABOUT_DATA.hero.tagline}
      </div>
      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-slate-800 via-purple-600 to-blue-600 dark:from-slate-100 dark:via-purple-400 dark:to-blue-400 bg-clip-text text-transparent mb-6">
        {ABOUT_DATA.hero.title}
      </h1>
      
      {/* Hero SEO Content */}
      <div className="max-w-4xl mx-auto mb-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
        <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
          {ABOUT_DATA.hero.seoContent}
        </p>
      </div>
      
      <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
        {ABOUT_DATA.hero.description}
      </p>
    </div>
  </div>
);

// Extracted MissionSection component
interface MissionSectionProps {
  renderFeatures: () => React.ReactNode;
  renderWhyChooseList: () => React.ReactNode;
}

const MissionSection: React.FC<MissionSectionProps> = ({ renderFeatures, renderWhyChooseList }) => (
  <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
        </div>
        <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
          {ABOUT_DATA.mission.title}
        </h2>
      </div>
      <div className="prose dark:prose-invert max-w-none">
        <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
          {ABOUT_DATA.mission.description}
        </p>
        <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed">
          {ABOUT_DATA.mission.vision}
        </p>
      </div>
      {renderFeatures()}
    </div>
    <div className="bg-gradient-to-br from-purple-500 to-blue-600 rounded-3xl p-8 text-white shadow-2xl">
      <h3 className="text-2xl font-bold mb-6">Why Choose TooliQ.ai?</h3>
      {renderWhyChooseList()}
    </div>
  </div>
);

// Extracted ToolCategoriesSection component
interface ToolCategoriesSectionProps {
  renderToolCategories: () => React.ReactNode;
}

const ToolCategoriesSection: React.FC<ToolCategoriesSectionProps> = ({ renderToolCategories }) => (
  <div className="mb-20">
    <div className="text-center mb-12">
      <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200 mb-4">
        Comprehensive AI Tool Categories
      </h2>
      
      {/* SEO Content */}
      <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
        <p className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed">
          Browse our <strong>150+ AI tool categories</strong> including <strong>AI writing tools, image generators, coding assistants, 
          voice & video AI, research tools, and no-code AI platforms</strong>. Each category is meticulously organized 
          to help you find exactly what you need for your specific use case.
        </p>
      </div>
    </div>
    {renderToolCategories()}
  </div>
);

// Extracted UserTypesSection component
interface UserTypesSectionProps {
  renderUserTypes: () => React.ReactNode;
}

const UserTypesSection: React.FC<UserTypesSectionProps> = ({ renderUserTypes }) => (
  <div className="mb-20">
    <div className="text-center mb-12">
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
          <Users className="w-6 h-6 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
          Who Uses TooliQ.ai?
        </h2>
      </div>
      
      {/* SEO Content for target audience */}
      <div className="max-w-3xl mx-auto mb-8 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
        <p className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed">
          Our platform serves <strong>50,000+ monthly users</strong> including <strong>developers, designers, marketers, 
          content creators, entrepreneurs, and researchers</strong>. Whether you're building AI applications, 
          creating content, analyzing data, or scaling your business, TooliQ.ai has the tools you need.
        </p>
      </div>
    </div>
    {renderUserTypes()}
  </div>
);

// Extracted TechnologyStackSection component
const TechnologyStackSection: React.FC = () => (
  <div className="mb-20 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-slate-200 dark:border-slate-700">
    <div className="text-center mb-8">
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
          <Cpu className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
        </div>
        <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
          Built with Modern Technology
        </h2>
      </div>
      <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
        Our platform leverages cutting-edge technology to deliver the best user experience
      </p>
    </div>
    <div className="grid md:grid-cols-3 gap-6 text-center">
      <div className="p-6 bg-slate-50 dark:bg-gray-700/50 rounded-2xl">
        <Database className="w-8 h-8 text-blue-600 dark:text-blue-400 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Real-time Database</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">Instant updates and live data synchronization</p>
      </div>
      <div className="p-6 bg-slate-50 dark:bg-gray-700/50 rounded-2xl">
        <Brain className="w-8 h-8 text-purple-600 dark:text-purple-400 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">AI-Powered Search</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">Smart recommendations and semantic search</p>
      </div>
      <div className="p-6 bg-slate-50 dark:bg-gray-700/50 rounded-2xl">
        <Zap className="w-8 h-8 text-green-600 dark:text-green-400 mx-auto mb-3" />
        <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">Fast Performance</h3>
        <p className="text-sm text-slate-600 dark:text-slate-400">Optimized for speed and seamless user experience</p>
      </div>
    </div>
  </div>
);

// Extracted ValuesSection component
const ValuesSection: React.FC = () => (
  <div className="mb-16 bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/10 dark:to-blue-900/10 rounded-3xl p-8 shadow-lg border border-slate-200 dark:border-slate-700">
    <div className="text-center mb-8">
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
          <Heart className="w-6 h-6 text-cyan-600 dark:text-cyan-400" />
        </div>
        <h2 className="text-3xl font-bold text-slate-800 dark:text-slate-200">
          Our Core Values
        </h2>
      </div>
    </div>
    <div className="grid md:grid-cols-3 gap-6">
      <div className="text-center p-6">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Trust & Transparency</h3>
        <p className="text-slate-600 dark:text-slate-400">We provide unbiased, honest reviews and clear information about every tool</p>
      </div>
      <div className="text-center p-6">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Community First</h3>
        <p className="text-slate-600 dark:text-slate-400">Our platform is built for and by the AI community</p>
      </div>
      <div className="text-center p-6">
        <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Rocket className="w-8 h-8 text-white" />
        </div>
        <h3 className="font-bold text-slate-800 dark:text-slate-200 mb-2">Innovation & Growth</h3>
        <p className="text-slate-600 dark:text-slate-400">We continuously evolve to meet the changing needs of the AI landscape</p>
      </div>
    </div>
  </div>
);

// Extracted NewsletterSection component
const NewsletterSection: React.FC = () => (
  <div className="mb-16 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-3xl p-8 shadow-lg border border-slate-200 dark:border-slate-700">
    <div className="text-center max-w-2xl mx-auto">
      <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <Zap className="w-8 h-8 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
        Stay Updated with AI Trends
      </h3>
      <p className="text-slate-600 dark:text-slate-400 mb-6">
        Subscribe to our newsletter for weekly AI tool discoveries, industry insights, and expert tips.
      </p>
      <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
        <input
          type="email"
          placeholder="Your email address"
          className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          type="submit"
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors"
        >
          Subscribe
        </button>
      </form>
    </div>
  </div>
);

// Extracted CTASection component
const CTASection: React.FC = () => (
  <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl p-8 md:p-12 text-white text-center shadow-2xl">
    <div className="max-w-2xl mx-auto">
      <Lightbulb className="w-12 h-12 mx-auto mb-6 text-white/80" />
      <h2 className="text-3xl font-bold mb-4">
        Ready to Discover AI Tools That Transform?
      </h2>
      <div className="prose prose-invert max-w-none mb-8">
        <p className="text-lg text-white/90 leading-relaxed">
          Join <strong>50,000+ professionals</strong> who use TooliQ.ai to find the perfect <strong>AI tools for development, 
          design, marketing, content creation, and business growth</strong>. Start exploring our comprehensive 
          directory of <strong>25,000+ AI applications</strong> today.
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a
          href="/tools"
          className="inline-flex items-center justify-center gap-2 bg-white text-purple-600 font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
        >
          <Search className="w-5 h-5" />
          Explore AI Tools Directory
        </a>
        <a
          href="/contact"
          className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white font-semibold px-8 py-4 rounded-xl hover:bg-white/10 transition-colors duration-200 transform hover:scale-105"
        >
          <ArrowRight className="w-5 h-5" />
          Contact Our Team
        </a>
      </div>
    </div>
  </div>
);

export default React.memo(About);